import type { Palavra } from './index';

/** Só o núcleo da palavra importa na comparação: pontuação e caixa variam. */
export function normalizar(palavra: string): string {
  return palavra
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

const JANELA = 25;

interface Emenda {
  /** Quantas palavras do novo trecho descartar por já estarem no anterior. */
  descartar: number;
  casadas: number;
}

/**
 * Acha onde o novo trecho repete o fim do anterior.
 *
 * A sobreposição de 1,5 s existe para o Whisper não cortar palavra no meio,
 * mas ela chega duplicada. Comparar tempo não resolve: o mesmo trecho de fala
 * ganha carimbos diferentes em requisições diferentes. Então a emenda é pelo
 * texto — a maior subsequência comum entre o fim de um e o começo do outro.
 */
export function acharEmenda(anteriores: Palavra[], novas: Palavra[]): Emenda {
  const cauda = anteriores.slice(-JANELA).map((p) => normalizar(p.p));
  const cabeca = novas.slice(0, JANELA).map((p) => normalizar(p.p));
  if (cauda.length === 0 || cabeca.length === 0) return { descartar: 0, casadas: 0 };

  // Maior sufixo da cauda que é prefixo da cabeça: é o formato que a
  // sobreposição produz, e evita casar palavras soltas espalhadas.
  const maximo = Math.min(cauda.length, cabeca.length);
  for (let tamanho = maximo; tamanho >= 2; tamanho -= 1) {
    const fim = cauda.slice(cauda.length - tamanho);
    const comeco = cabeca.slice(0, tamanho);
    if (fim.every((palavra, i) => palavra === comeco[i] && palavra.length > 0)) {
      return { descartar: tamanho, casadas: tamanho };
    }
  }

  // Uma palavra só: aceita apenas se for longa, porque "e" ou "de" casam com
  // qualquer coisa e cortariam fala de verdade.
  const ultima = cauda[cauda.length - 1];
  if (ultima && ultima.length >= 4 && cabeca[0] === ultima) {
    return { descartar: 1, casadas: 1 };
  }
  return { descartar: 0, casadas: 0 };
}

/** Junta o novo trecho ao acumulado, sem repetir a sobreposição. */
export function mesclarPalavras(anteriores: Palavra[], novas: Palavra[]): Palavra[] {
  const { descartar } = acharEmenda(anteriores, novas);
  return [...anteriores, ...novas.slice(descartar)];
}

/** Desloca os tempos de um trecho para a linha do tempo da gravação inteira. */
export function deslocar(palavras: Palavra[], offsetMs: number): Palavra[] {
  return palavras.map((p) => ({ p: p.p, i: p.i + offsetMs, f: p.f + offsetMs }));
}

/** Texto legível a partir das palavras, para quem só quer ler. */
export function textoDe(palavras: Palavra[]): string {
  return palavras
    .map((p) => p.p)
    .join(' ')
    .replace(/\s+([,.!?;:])/g, '$1')
    .trim();
}
